'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Toast } from '@/ui/common/Toast/Toast';
import { LinkIcon } from '@/ui/common/Links/LinkIcon';
import { CountdownTimerPopup } from '@/ui/common/Popups/CountdownTimerPopup';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { useStickyElementById } from '@/service/hooks/useStickyElementById';
import { POPUP_OPENING_TIME_MINUTES } from '@/service/constants/variables';
import { ECartActionType } from '@/service/state/Cart/domain';
import { cn } from '@/service/utils/cn';
import { SideMenuButton } from './core';
import { ToastScreen } from '../CartContent/core/ToastScreen';
import {
    getCommonPhoneFormat,
    getCommonPhoneFormatURI,
} from '@/service/utils/getCommonPhoneFormat';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import Link from 'next/link';
import { SearchBox } from '../SearchBox/SearchBox';
import { useAttractionsStore } from '@/service/state/Attractions/useAttractionsStore';
import { ETripView } from '@/domain/Activities.domain';

type TLayoutProps = {
    partnerSlug: string;
    partnerWebsite: string;
    phoneNumber: string;
    partnerLogoSrc: string;
};

export const LayoutTop = ({
    partnerSlug,
    partnerWebsite,
    phoneNumber,
    partnerLogoSrc,
}: TLayoutProps) => {
    const router = useRouter();
    const pathname = usePathname();

    const [extended, setExtended] = useState(false);

    const { cart, getCart, extCart, success, cartToastIsShown, showThenHideCartToast } =
        useCartStore((state) => state);

    const { maintenance, phone } = usePartnerStore();
    const handleTripViewChange = useAttractionsStore((state) => state.handleTripViewChange);

    useEffect(() => {
        setExtended(
            success.actionType === ECartActionType.EXTEND_TIME_FOR_CART && !!success.message
        );
    }, [success.actionType, success.message]);

    const fetchCart = useCallback(
        () => getCart({ partnerId: partnerSlug }, ECartActionType.GET_CART),
        [getCart, partnerSlug]
    );
    const extendTimeByFiveMinutes = useCallback(
        () => extCart({ partnerId: partnerSlug }),
        [extCart, partnerSlug]
    );
    const goToCheckoutPage = useCallback(
        () => router.push(`/${partnerSlug}/checkout`),
        [partnerSlug, router]
    );

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const { isSticky } = useStickyElementById('sticky-buttons', { offset: 20 });
    const isMainPage = pathname.split('/').length <= 3;
    const isBillPage = pathname.includes('/bill/');
    const hideLogo = isSticky && isMainPage;

    return (
        <>
            <Toast
                show={cartToastIsShown}
                screen={
                    <ToastScreen
                        title={'Your cart is empty!'}
                        description={
                            'Lets find some wonderful activities to do. Get ready to be inspired...'
                        }
                    />
                }
            />
            <>
                <div className="h-14 flex-shrink-0 md:h-16">
                    <div
                        data-test-id="header"
                        className={cn(
                            'shadow-main fixed left-0 right-0 top-0 z-40 h-14 bg-blue-main dark:bg-dark-3 md:h-16',
                            hideLogo ? 'z-40' : 'z-50 shadow-none dark:shadow-none'
                        )}
                    >
                        <div className="wrapper flex justify-between px-2 py-3 md:px-8 md:py-4">
                            <div className="flex h-8 shrink-0">
                                <SideMenuButton
                                    partnerWebsite={partnerWebsite}
                                    partnerLogoSrc={partnerLogoSrc}
                                    phoneNumber={phoneNumber}
                                    partnerSlug={partnerSlug}
                                    data-test-id="menu-btn"
                                />
                                <LinkIcon
                                    href={`/${partnerSlug}/`}
                                    iconSrc={partnerLogoSrc}
                                    className={cn(
                                        'relative left-0 top-[-8px] mt-1 h-10 w-fit max-w-[168px] overflow-hidden rounded shadow-dark transition-transform md:top-[-16px] md:mt-2 md:h-12 md:max-w-[220px] [&>*:nth-child(1)]:w-auto',
                                        hideLogo ? 'lg:translate-y-0' : ''
                                    )}
                                    alt={partnerSlug}
                                    data-test-id="partner-logo"
                                    onError={({ target }) => {
                                        const e = target as HTMLImageElement;
                                        e.src = '/img/PoweredBy.png';
                                    }}
                                    onClick={() =>
                                        handleTripViewChange(ETripView.BROWSE_BY_ACTIVITY)
                                    }
                                />
                            </div>
                            <div className="flex h-8 w-full md:max-w-fit">
                                <SearchBox />
                                <LinkIcon
                                    href={getCommonPhoneFormatURI(phoneNumber)}
                                    target="_blank"
                                    iconSrc="/icon/Phone.svg"
                                    className="ml-3 w-8 shrink-0 py-1 hover:py-0"
                                    alt="Phone"
                                    data-test-id="phone-btn"
                                />
                                {!isBillPage && (
                                    <LinkIcon
                                        href={`/${partnerSlug}/cart/`}
                                        onClick={(e) => {
                                            if (!cart?.cart_items.length) {
                                                e.preventDefault();
                                                showThenHideCartToast();
                                            }
                                        }}
                                        iconSrc="/icon/Cart.svg"
                                        className="relative ml-1 w-8 shrink-0 py-1 hover:py-0 md:ml-3"
                                        alt="Cart"
                                        child={
                                            !!cart?.cart_items.length && (
                                                <span className="icon-status-badge">
                                                    {cart.cart_items.length}
                                                </span>
                                            )
                                        }
                                        data-test-id="cart-btn"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {!!maintenance && (
                    <div role="alert">
                        <div className="wrapper tx-heading-2 bg-state-error p-4 text-center font-bold text-white">
                            {maintenance}
                        </div>
                        <div className="wrapper rounded-b border border-t-0 border-red-400 bg-red-100 px-4 py-3 text-center text-red-700">
                            <p>
                                You may call us:{' '}
                                <Link href={getCommonPhoneFormatURI(phone)}>
                                    <b className="inline-block">{getCommonPhoneFormat(phone)}</b>
                                </Link>
                            </p>
                        </div>
                    </div>
                )}
            </>
            {!isBillPage && (
                <CountdownTimerPopup
                    timeLeft={cart?.time_left && cart.time_left > 0 ? cart.time_left : 0}
                    onCountdownEnd={fetchCart}
                    popupOpeningTimeMinutes={POPUP_OPENING_TIME_MINUTES}
                    withActionButtons
                    closePopupAfterAction
                    countdownIsExtended={extended}
                    primaryButtonText="Extend time by 2 minutes"
                    secondaryButtonText="Checkout now"
                    onClickPrimaryButton={extendTimeByFiveMinutes}
                    onClickSecondaryButton={goToCheckoutPage}
                />
            )}
        </>
    );
};
